/**
 * Door watch — guards and the janitor remember which doors they have personally
 * seen CLOSED, and re-close them if the player leaves one open. Purely headless
 * (CLAUDE.md rule 4): this module only updates a watcher's memory and picks the
 * door it should go shut. The walk-over-and-close action lives in the movement
 * layer (`tryCloseKnownDoor` in guard.ts, driven from updateGuard / updateJanitor).
 *
 * "Only if they've known it closed": a watcher reacts to an OPEN door solely when
 * that exact tile is already in its `knownClosedDoors` set — a door it first met
 * standing open never triggers. Because every door spawns closed, watchers build
 * this memory naturally as they patrol past them.
 */

import { CONFIG } from '../config';
import { perceivedLight } from './light';
import { tileAt, type MapData } from './map';
import { TileType } from './tiles';

/** Key for a door position in a watcher's memory set. */
export function doorKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * The structural slice of a Guard/Janitor that door-watching reads and writes.
 * Kept minimal so the memory logic is testable with a plain object fixture.
 */
export interface DoorWatcher {
  x: number;
  y: number;
  /** The roaming floor captain OPENS doors as it transits — it never fusses over
   *  closing them, so it records memory but never acquires a close target. */
  isCaptain: boolean;
  /** Sees in the dark → can still make out a door's state without light. */
  nightVision: boolean;
  /** Current vision-cone tile indices, or null when unavailable (dead/off). */
  fovTiles: Set<number> | null;
  knownClosedDoors: Set<string>;
  closeDoorTarget: { x: number; y: number } | null;
  doorCloseCooldown: number;
}

function cheb(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

/**
 * Refresh a watcher's door memory from its current vision cone and, unless it's
 * the captain, target the nearest known-closed-now-open door within reach to go
 * shut. Call once per tick AFTER the awareness pass has rebuilt `fovTiles`.
 */
export function updateDoorMemory(w: DoorWatcher, map: MapData): void {
  if (w.doorCloseCooldown > 0) w.doorCloseCooldown -= 1;

  // Forget a target that someone already closed (or that changed out from under us).
  if (w.closeDoorTarget) {
    const { x, y } = w.closeDoorTarget;
    if (tileAt(map, x, y) !== TileType.Door) w.closeDoorTarget = null;
  }

  const fov = w.fovTiles;
  if (!fov) return;

  let best: { x: number; y: number } | null = w.closeDoorTarget;
  let bestDist = best ? cheb(w.x, w.y, best.x, best.y) : Infinity;

  for (const d of map.doors) {
    const idx = d.y * map.width + d.x;
    if (!fov.has(idx)) continue;
    // Can't read a door's state in the dark.
    if (perceivedLight(map, d.x, d.y, w.nightVision) < CONFIG.light.hideBelow) continue;

    const t = tileAt(map, d.x, d.y);
    const key = doorKey(d.x, d.y);
    if (t === TileType.DoorClosed) {
      w.knownClosedDoors.add(key); // this is how we know it *should* be shut
      continue;
    }
    if (t !== TileType.Door) continue; // some non-openable state — ignore

    // It's OPEN. React only if we knew it closed, we're a stationed watcher (not
    // the captain), it's nearby/relevant, and we're off cooldown.
    if (w.isCaptain || w.doorCloseCooldown > 0) continue;
    if (!w.knownClosedDoors.has(key)) continue;
    const dist = cheb(w.x, w.y, d.x, d.y);
    if (dist > CONFIG.guard.doorWatch.radius) continue;
    if (dist < bestDist) {
      best = { x: d.x, y: d.y };
      bestDist = dist;
    }
  }
  w.closeDoorTarget = best;
}
